import * as THREE from 'three'

/** `userData` 键：避免对同一材质重复注入 `onBeforeCompile` */
const IBL_DIFFUSE_ONLY_PATCH_KEY = '__imockupIblDiffuseOnlyPatch'
/** 补丁前保存的 `onBeforeCompile`，供经典光恢复完整 IBL 时还原 */
const previousOnBeforeCompileByMaterial = new WeakMap<
  THREE.MeshStandardMaterial,
  THREE.MeshStandardMaterial['onBeforeCompile']
>()

/**
 * 与 Three.js `lights_fragment_maps.glsl.js` 中 `RE_IndirectDiffuse` 段一致，
 * 但**不包含**对 `radiance` / `clearcoatRadiance` 的 `getIBLRadiance` 累加，
 * 使 HDR `scene.environment` 仅贡献漫反射型 IBL（`iblIrradiance`），不参与镜面环境反射。
 *
 * @see https://github.com/mrdoob/three.js/blob/dev/src/renderers/shaders/ShaderChunk/lights_fragment_maps.glsl.js
 */
const LIGHTS_FRAGMENT_MAPS_DIFFUSE_IBL_ONLY = /* glsl */ `
#if defined( RE_IndirectDiffuse )

	#ifdef USE_LIGHTMAP

		vec4 lightMapTexel = texture2D( lightMap, vLightMapUv );
		vec3 lightMapIrradiance = lightMapTexel.rgb * lightMapIntensity;

		irradiance += lightMapIrradiance;

	#endif

	#if defined( USE_ENVMAP ) && defined( ENVMAP_TYPE_CUBE_UV )

		#if defined( STANDARD ) || defined( LAMBERT ) || defined( PHONG )

			iblIrradiance += getIBLIrradiance( geometryNormal );

		#endif

	#endif

#endif
`

/**
 * 为 `MeshStandardMaterial` / `MeshPhysicalMaterial` 注入片段着色器补丁：
 * HDR 环境仍参与间接漫反射照明，但不参与环境镜面反射（机身不再「映出」全景图）。
 *
 * @param material - 网格材质（非标准材质则忽略）
 */
export function patchMaterialForDiffuseIblOnly(material: THREE.Material): void {
  if (!(material instanceof THREE.MeshStandardMaterial)) return
  const mark = material.userData as Record<string, boolean | undefined>
  if (mark[IBL_DIFFUSE_ONLY_PATCH_KEY]) return
  mark[IBL_DIFFUSE_ONLY_PATCH_KEY] = true

  previousOnBeforeCompileByMaterial.set(material, material.onBeforeCompile)
  const previous = material.onBeforeCompile
  material.onBeforeCompile = (shader, renderer) => {
    previous?.(shader, renderer)
    shader.fragmentShader = shader.fragmentShader.replace(
      '#include <lights_fragment_maps>',
      LIGHTS_FRAGMENT_MAPS_DIFFUSE_IBL_ONLY,
    )
  }
  material.needsUpdate = true
}

/**
 * 还原 {@link patchMaterialForDiffuseIblOnly}，使材质恢复完整环境镜面 IBL（经典光 / RoomEnvironment）。
 *
 * @param material - 网格材质（非标准材质则忽略）
 */
export function unpatchMaterialForDiffuseIblOnly(material: THREE.Material): void {
  if (!(material instanceof THREE.MeshStandardMaterial)) return
  const mark = material.userData as Record<string, unknown>
  if (!mark[IBL_DIFFUSE_ONLY_PATCH_KEY]) return

  material.onBeforeCompile = previousOnBeforeCompileByMaterial.get(material) ?? (() => {})
  previousOnBeforeCompileByMaterial.delete(material)
  delete mark[IBL_DIFFUSE_ONLY_PATCH_KEY]
  material.needsUpdate = true
}

/**
 * 按 HDR 预设策略对单材质打补丁或还原。
 *
 * @param material - 网格材质
 * @param diffuseIblOnly - `true` 时仅漫反射 IBL（HDR 预设）；`false` 时完整 IBL（经典光）
 */
export function applyDiffuseIblOnlyPolicy(material: THREE.Material, diffuseIblOnly: boolean): void {
  if (diffuseIblOnly) patchMaterialForDiffuseIblOnly(material)
  else unpatchMaterialForDiffuseIblOnly(material)
}

/**
 * 对子树中所有网格的标准/物理材质执行 {@link patchMaterialForDiffuseIblOnly}。
 *
 * @param root - glTF 根或图层 `Group`
 */
export function patchObjectTreeForDiffuseIblOnly(root: THREE.Object3D): void {
  applyDiffuseIblOnlyPolicyOnTree(root, true)
}

/**
 * 对子树中所有网格材质按 IBL 策略打补丁或还原。
 *
 * @param root - glTF 根或图层 `Group`
 * @param diffuseIblOnly - 见 {@link applyDiffuseIblOnlyPolicy}
 */
export function applyDiffuseIblOnlyPolicyOnTree(root: THREE.Object3D, diffuseIblOnly: boolean): void {
  root.traverse((obj) => {
    if (!(obj instanceof THREE.Mesh)) return
    const mats = obj.material
    if (Array.isArray(mats)) mats.forEach((m) => applyDiffuseIblOnlyPolicy(m, diffuseIblOnly))
    else applyDiffuseIblOnlyPolicy(mats, diffuseIblOnly)
  })
}
